<template>
  <v-card class="anc-card mb-4" outlined>
    <!-- ส่วนหัว: ระดับสิทธิ์ + ภารกิจ + ตัวเลขสรุป -->
    <div class="anc-header pa-4">
      <div class="d-flex flex-wrap align-center">
        <v-icon color="primary" class="mr-2">mdi-bell-badge-outline</v-icon>
        <h3 class="anc-title mr-3">ศูนย์แจ้งเตือนผู้ดูแลระบบ</h3>
        <v-chip
          small
          :color="data.is_superadmin ? 'deep-purple' : 'primary'"
          dark
          class="mr-2"
        >
          <v-icon left small>
            {{ data.is_superadmin ? "mdi-shield-crown" : "mdi-shield-account" }}
          </v-icon>
          {{ data.is_superadmin ? "SuperAdmin : ทั้งระบบ" : "Admin ตามภารกิจ" }}
        </v-chip>
        <v-spacer />
        <span v-if="data.today" class="caption grey--text mr-2">
          ข้อมูล ณ {{ thaiDate(data.today) }}
        </span>
        <v-btn icon small :loading="loading" @click="load" title="โหลดใหม่">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <div v-if="!data.is_superadmin && data.missions.length" class="mt-2">
        <span class="caption grey--text text--darken-1 mr-1">ภารกิจที่ได้รับมอบหมาย:</span>
        <v-chip
          v-for="m in data.missions"
          :key="m.key"
          x-small
          outlined
          color="primary"
          class="mr-1 mb-1"
        >
          {{ m.label }}
        </v-chip>
      </div>

      <v-row dense class="mt-3">
        <v-col v-for="k in kpis" :key="k.key" cols="6" md="3">
          <div
            class="anc-kpi"
            :class="{ 'anc-kpi--active': filter === k.key }"
            :style="{ borderLeftColor: k.hex }"
            role="button"
            @click="toggleFilter(k.key)"
          >
            <div class="anc-kpi-value" :style="{ color: k.hex }">{{ k.value }}</div>
            <div class="anc-kpi-label">{{ k.label }}</div>
          </div>
        </v-col>
      </v-row>
    </div>

    <v-alert v-if="error" type="warning" text dense class="ma-4 mb-0">
      {{ error }}
    </v-alert>

    <v-row no-gutters>
      <!-- กำหนดการเปิด/ปิดระบบ -->
      <v-col cols="12" md="6" class="pa-4 anc-col">
        <div class="anc-section-title">
          <v-icon small color="teal darken-1" class="mr-1">mdi-calendar-clock</v-icon>
          สถานะกำหนดการเปิด/ปิดระบบ
        </div>
        <v-skeleton-loader v-if="loading && !loaded" type="list-item-two-line@3" />
        <div v-else-if="!visiblePeriods.length" class="anc-empty">
          <v-icon color="success" class="mr-1">mdi-check-circle-outline</v-icon>
          ไม่มีกำหนดการที่ต้องติดตาม
        </div>
        <v-list v-else dense class="py-0 anc-list">
          <v-list-item
            v-for="p in visiblePeriods"
            :key="p.source + p.id"
            :to="p.route"
            class="anc-item"
          >
            <v-list-item-icon class="mr-3">
              <v-icon :color="stateMeta(p.state).color">{{ stateMeta(p.state).icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="anc-item-title">{{ p.title }}</v-list-item-title>
              <v-list-item-subtitle>
                รอบ {{ p.times }}/{{ p.year }} · {{ thaiDate(p.start) }} – {{ thaiDate(p.stop) }}
                <span class="ml-1">· {{ missionLabel(p.mission) }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="ml-2">
              <v-chip x-small :color="stateMeta(p.state).color" dark>
                {{ stateText(p) }}
              </v-chip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div v-if="hiddenPeriodCount" class="text-center mt-1">
          <v-btn x-small text color="primary" @click="showAllPeriods = !showAllPeriods">
            {{ showAllPeriods ? "แสดงน้อยลง" : `แสดงทั้งหมด (+${hiddenPeriodCount})` }}
          </v-btn>
        </div>
        <v-alert
          v-if="data.is_superadmin && data.summary.period_overdue > 0"
          type="error"
          text
          dense
          class="mt-3 mb-0 caption"
        >
          มี {{ data.summary.period_overdue }} กำหนดการที่เลยวันสิ้นสุดแต่ยังเปิดอยู่ —
          ผู้ใช้ยังบันทึก/แก้ไขข้อมูลได้ ควรตรวจสอบและปิดกำหนดการ
        </v-alert>
      </v-col>

      <!-- คำร้องขอยกเลิก/แก้ไขที่รอดำเนินการ -->
      <v-col cols="12" md="6" class="pa-4">
        <div class="anc-section-title">
          <v-icon small color="deep-orange darken-1" class="mr-1">mdi-file-document-edit-outline</v-icon>
          คำร้องขอยกเลิก/แก้ไข ที่รอดำเนินการ
        </div>
        <v-skeleton-loader v-if="loading && !loaded" type="list-item-two-line@3" />
        <div v-else-if="!pendingRequests.length" class="anc-empty">
          <v-icon color="success" class="mr-1">mdi-check-circle-outline</v-icon>
          ไม่มีคำร้องค้างดำเนินการ
        </div>
        <v-list v-else dense class="py-0 anc-list">
          <v-list-item
            v-for="r in pendingRequests"
            :key="r.key"
            :to="r.route"
            class="anc-item"
          >
            <v-list-item-icon class="mr-3">
              <v-icon :color="sevColor(r.severity)">mdi-file-alert-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="anc-item-title">{{ r.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ r.mission_label }} · {{ r.detail }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="ml-2 flex-row align-center">
              <v-chip small :color="sevColor(r.severity)" dark class="font-weight-bold mr-1">
                {{ r.count }}
              </v-chip>
              <v-icon small color="grey">mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div v-if="loaded && clearRequests.length" class="caption grey--text mt-2">
          <v-icon x-small color="success">mdi-check</v-icon>
          ตรวจแล้วไม่มีคำร้องค้าง {{ clearRequests.length }} ประเภท
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
// ศูนย์แจ้งเตือนผู้ดูแลระบบ — ใช้ข้อมูลจาก admin_notification_center.php
// (backend กรองตามภารกิจของผู้ใช้จาก session แล้ว ฝั่งนี้แสดงผลอย่างเดียว)
const STATE_META = {
  overdue: { color: "red darken-1", icon: "mdi-alert-octagon", text: "เลยกำหนดแต่ยังเปิด" },
  should_open: { color: "orange darken-2", icon: "mdi-calendar-alert", text: "ถึงกำหนดแต่ยังปิด" },
  closing_soon: { color: "amber darken-3", icon: "mdi-timer-sand", text: "ใกล้ปิด" },
  early_open: { color: "blue darken-1", icon: "mdi-calendar-start", text: "เปิดก่อนกำหนด" },
  open: { color: "green darken-1", icon: "mdi-calendar-check", text: "เปิดอยู่" },
  upcoming: { color: "indigo lighten-1", icon: "mdi-calendar-arrow-right", text: "กำลังจะเปิด" },
  recently_closed: { color: "grey", icon: "mdi-calendar-remove", text: "เพิ่งปิด" }
};
const SEV_COLOR = { error: "red darken-1", warning: "orange darken-2", info: "blue darken-1" };
const PERIOD_PREVIEW = 6;

export default {
  name: "AdminNotificationCenter",

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      loaded: false,
      error: "",
      filter: null,
      showAllPeriods: false,
      data: {
        is_superadmin: false,
        missions: [],
        summary: {},
        periods: [],
        requests: []
      }
    };
  },

  computed: {
    kpis() {
      const s = this.data.summary || {};
      return [
        { key: "requests", label: "คำร้องรอดำเนินการ", value: s.request_total || 0, hex: "#e64a19" },
        { key: "open", label: "กำหนดการเปิดอยู่", value: (s.period_open || 0) + (s.period_closing_soon || 0) + (s.period_early_open || 0), hex: "#43a047" },
        { key: "attention", label: "กำหนดการต้องตรวจสอบ", value: (s.period_overdue || 0) + (s.period_should_open || 0), hex: "#e53935" },
        { key: "upcoming", label: "กำลังจะเปิด (30 วัน)", value: s.period_upcoming || 0, hex: "#5c6bc0" }
      ];
    },
    filteredPeriods() {
      const groups = {
        open: ["open", "closing_soon", "early_open"],
        attention: ["overdue", "should_open"],
        upcoming: ["upcoming"]
      };
      const allow = groups[this.filter];
      return allow ? this.data.periods.filter(p => allow.includes(p.state)) : this.data.periods;
    },
    visiblePeriods() {
      return this.showAllPeriods ? this.filteredPeriods : this.filteredPeriods.slice(0, PERIOD_PREVIEW);
    },
    hiddenPeriodCount() {
      return Math.max(0, this.filteredPeriods.length - PERIOD_PREVIEW);
    },
    pendingRequests() {
      return this.data.requests.filter(r => r.count > 0);
    },
    clearRequests() {
      return this.data.requests.filter(r => r.count === 0);
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await this.$http.post("admin_notification_center.php", { ApiKey: this.ApiKey });
        if (res.data && res.data.status) {
          this.data = res.data;
          this.loaded = true;
          this.$emit("loaded", res.data);
        } else {
          this.error = (res.data && res.data.message) || "ไม่สามารถโหลดการแจ้งเตือนได้";
        }
      } catch (e) {
        // 401 ถูกจัดการโดย interceptor ใน main.js แล้ว (แจ้ง session หมดอายุ)
        this.error = "ไม่สามารถโหลดการแจ้งเตือนได้ กรุณาลองใหม่อีกครั้ง";
      } finally {
        this.loading = false;
      }
    },
    toggleFilter(key) {
      if (key === "requests") return;
      this.filter = this.filter === key ? null : key;
      this.showAllPeriods = false;
    },
    stateMeta(state) {
      return STATE_META[state] || STATE_META.open;
    },
    stateText(p) {
      const base = this.stateMeta(p.state).text;
      if ((p.state === "open" || p.state === "closing_soon") && p.days_to_stop !== null) {
        return p.days_to_stop === 0 ? "ปิดวันนี้" : `${base} · เหลือ ${p.days_to_stop} วัน`;
      }
      if (p.state === "overdue") return `${base} ${-p.days_to_stop} วัน`;
      if (p.state === "upcoming") return `อีก ${p.days_to_start} วัน`;
      return base;
    },
    sevColor(sev) {
      return SEV_COLOR[sev] || SEV_COLOR.info;
    },
    missionLabel(key) {
      const m = this.data.missions.find(x => x.key === key);
      return m ? m.label : key;
    },
    thaiDate(raw) {
      if (!raw) return "-";
      const d = new Date(String(raw).substring(0, 10) + "T00:00:00");
      if (isNaN(d)) return raw;
      return d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" });
    }
  }
};
</script>

<style scoped>
.anc-card {
  border-radius: 14px !important;
  overflow: hidden;
  font-family: "Sarabun", "Noto Sans Thai", sans-serif;
}
.anc-header {
  background: linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.anc-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #212121;
}
.anc-kpi {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 4px solid;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.anc-kpi:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}
.anc-kpi--active {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.35);
}
.anc-kpi-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
}
.anc-kpi-label {
  font-size: 0.8rem;
  color: #616161;
}
.anc-col {
  border-right: 1px solid rgba(0, 0, 0, 0.06);
}
@media (max-width: 959px) {
  .anc-col {
    border-right: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
}
.anc-section-title {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 8px;
  color: #37474f;
}
.anc-list {
  max-height: 420px;
  overflow-y: auto;
}
.anc-item {
  border-radius: 8px;
  margin-bottom: 2px;
}
.anc-item-title {
  font-weight: 500;
  white-space: normal;
}
.anc-empty {
  color: #616161;
  font-size: 0.9rem;
  padding: 12px 0;
}
</style>
